import * as DateFNS from 'date-fns';
import {
  PricingByPropertyIdFragment,
  PricingByPropertyIdFragmentDoc,
  TravelCurrencyCode,
  TravelPricingsForAccommodationByPropertyIdPayload,
  useAccommodationPagePricingQuery
} from '@graphql/generated';
import { ApolloClient, ApolloError, makeVar, useApolloClient, useReactiveVar } from '@apollo/client';
import { useEffect, useMemo, useRef } from 'react';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';
import { addError } from '@shared/utils/logger';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useTravelWhiteLabelCurrency } from '../useTravelWhiteLabelCurrency';

const queryStatusVar = makeVar<{
  loading: boolean;
  called: boolean;
  error?: ApolloError;
}>({ loading: false, called: false, error: undefined });

const translatedPrice = (priceInMinorUnits: number, digits: number, currency: string, locale: NavigatorLanguage['language'] = 'en-US') => {
  return Math.round(priceInMinorUnits / Math.pow(10, digits)).toLocaleString(locale, {
    style: 'currency',
    maximumSignificantDigits: 3,
    currency
  });
};

/**
 *
 * @param joyPlaceIds - array of joyPlaceIds to fetch pricing for
 * @param targetDate - optional date (in milliseconds) for check-in and check-out (-1 day and +1 day from event date respectively)
 */
export const usePricingByJoyPlaceIdQuery = (joyPlaceIds: string[], targetDate?: Maybe<number>) => {
  const checkInDate = targetDate ? DateFNS.format(DateFNS.subDays(targetDate, 1), 'yyyy-MM-dd') : null;
  const checkOutDate = targetDate ? DateFNS.format(DateFNS.addDays(targetDate, 1), 'yyyy-MM-dd') : null;
  const [currency, { loading: loadingCurrency }] = useTravelWhiteLabelCurrency();
  const [localeBasedCurrencyEnabled, loadingUseLocaleCurrencyFlag] = useIsFeatureEnabled('localeBasedHotelCurrency');
  const [requestPricingEnabled, loadingShowPricingFlag] = useIsFeatureEnabled('travelShowHotelPrice');

  const payload: TravelPricingsForAccommodationByPropertyIdPayload | undefined = useMemo(() => {
    if (loadingCurrency || !targetDate || !checkInDate || !checkOutDate || !joyPlaceIds.length || loadingUseLocaleCurrencyFlag || !requestPricingEnabled) {
      return undefined;
    }
    if (DateFNS.isBefore(targetDate, new Date())) {
      return undefined;
    }
    return {
      currencyCode: localeBasedCurrencyEnabled ? currency : TravelCurrencyCode.USD,
      dates: {
        checkIn: {
          // validated with skip
          high: checkInDate,
          low: checkInDate
        },
        // validated with skip
        checkOut: {
          high: checkOutDate,
          low: checkOutDate
        }
      },
      occupancies: {
        occupancies: [
          {
            adults: 2,
            childrenAges: []
          }
        ],
        unitCount: 1
      },
      propertyIdentifiers: joyPlaceIds
        .map(joyPlaceId => ({
          joyPlaceId: joyPlaceId || null
        }))
        .filter(joyPlaceId => joyPlaceId.joyPlaceId !== null)
    };
  }, [checkInDate, checkOutDate, currency, joyPlaceIds, loadingCurrency, loadingUseLocaleCurrencyFlag, localeBasedCurrencyEnabled, requestPricingEnabled, targetDate]);

  const { loading, called, error } = useAccommodationPagePricingQuery({
    batchMode: 'fast',
    // skip: !payload prevents the query from being called with undefined payload
    variables: {
      payload: payload!
    },
    onError: error => {
      addError(error, {
        context: 'usePricingByPropertyIdsQuery'
      });
    },
    skip: !payload
  });

  useEffect(() => {
    queryStatusVar({ loading: loading || loadingUseLocaleCurrencyFlag || loadingShowPricingFlag, called, error });
  }, [loading, loadingUseLocaleCurrencyFlag, loadingShowPricingFlag, called, error]);

  return {
    loading: loading || loadingUseLocaleCurrencyFlag || loadingShowPricingFlag,
    called,
    error: error
  };
};

const readFragmentFromCache = (client: ApolloClient<object>, joyPlaceId: string) => {
  // Define the fragment
  const fragment = PricingByPropertyIdFragmentDoc;

  // Generate the cache ID based on the type and key fields
  const cacheId = `TravelPricingByPropertyIdItem:{"property":{"joyPlaceId":"${joyPlaceId}"}}`;

  // Read the fragment from the cache
  const data = client.cache.readFragment<PricingByPropertyIdFragment>({
    id: cacheId,
    fragment
  });

  return data;
};

/**
 *
 * @param joyPlaceId - joyPlaceId to fetch pricing for
 * @returns
 */
export const usePricingByJoyPlaceIdFragment = (joyPlaceId: Maybe<string>): { price: Maybe<string>; strikeOutPrice: Maybe<string>; loading: boolean; error?: Error } => {
  // Cannot use useFragment because we are blocked by Node version <18
  // const { data, complete } = useFragment<PricingByPropertyIdFragment>({
  //   fragment: PricingByPropertyIdFragmentDoc,
  //   fragmentName: 'PricingByPropertyId',
  //   from: {
  //     __typename: 'TravelPricingByPropertyIdItem',
  //     property: {
  //       __typename: 'AccommodationCatalogProperty',
  //       joyPlaceId: joyPlaceId
  //     }
  //   }
  // });
  const complaintRef = useRef<boolean>(false);
  const client = useApolloClient();
  const locale = useLocaleContext();
  const { loading, error } = useReactiveVar(queryStatusVar);
  if (loading) {
    return { price: undefined, strikeOutPrice: undefined, loading: true };
  }
  if (error) {
    return { price: null, strikeOutPrice: undefined, loading: false, error: error };
  }
  const data = readFragmentFromCache(client, joyPlaceId!);

  const priceData = data?.pricingData?.perNightFinalAmountBeforeTaxesAndFees;
  const minorUnits = priceData?.valueInMinorUnits;
  const minorUnitsDigits = priceData?.digits;
  const currency = priceData?.currency;
  const price = (joyPlaceId && (minorUnits && currency ? translatedPrice(minorUnits, minorUnitsDigits!, currency, locale.language) : null)) || undefined;

  const strikeOutPriceData = data?.pricingData?.perNightOriginalAmountBeforeTaxesAndFees;
  const strikeOutMinorUnits = strikeOutPriceData?.valueInMinorUnits;
  const strikeOutMinorUnitsDigits = strikeOutPriceData?.digits;
  const strikeOutCurrency = strikeOutPriceData?.currency;
  const strikeOutPriceValid = strikeOutMinorUnits && minorUnits && strikeOutMinorUnits > minorUnits;

  if (strikeOutMinorUnits && !strikeOutPriceValid && !complaintRef.current) {
    complaintRef.current = true;
    addError(new Error('Strikeout price is not valid'), {
      context: 'usePricingByJoyPlaceIdFragment',
      strikeOutPriceUnits: strikeOutMinorUnits,
      priceUnits: minorUnits,
      joyPlaceId: joyPlaceId
    });
  }
  const strikeOutPrice =
    strikeOutPriceValid && strikeOutMinorUnits && strikeOutCurrency
      ? translatedPrice(strikeOutMinorUnits, strikeOutMinorUnitsDigits!, strikeOutCurrency, locale.language)
      : undefined;
  return { price, strikeOutPrice, loading: loading };
};
